import { parseSanitizedHtml } from "@/utils/sanitizeHtml";
import { useState, type ReactNode } from "react";
import { MdClose, MdWarning } from "react-icons/md";
import "@style/Admin.scss";
import "@style/sass/_confirmModal.scss";
import Modal from "@modalSrc/Modal"; 

type ConfirmModalProps = {
  isOpen: boolean;
  onConfirm?: () => void;
  onCancel?: () => void;
  title?: string | ReactNode;
  message: string;
  confirmText?: string;
  cancelText?: string;
  showCancelButton?: boolean;
  dangerText?: string;
  showSecondStep?: boolean;
  secondStepMessage?: string;
  secondStepWarning?: string;
  secondStepDetail?: string;
  secondStepConfirmText?: string;
  extraActions?: Array<{
    label: string;
    onClick: () => void;
    className?: string;
  }>;
  zIndex?: number;
};

export default function ConfirmModal({
  isOpen,
  onConfirm,
  onCancel,
  title = "Confirm Action",
  message,
  confirmText = "Yes, I'm sure",
  cancelText = "Cancel",
  showCancelButton = true,
  dangerText = "This action cannot be undone",
  showSecondStep = false,
  secondStepMessage = "Are you absolutely sure?",
  secondStepWarning = "This action is permanent and cannot be undone.",
  secondStepDetail = "Please confirm one more time that you want to proceed.",
  secondStepConfirmText = "Yes, Delete Permanently",
  extraActions = [],
  zIndex
}: ConfirmModalProps) {
  const [step, setStep] = useState(1);

  const handleFirstConfirm = () => {
    if (showSecondStep) {
      setStep(2);
    } else {
      handleFinalConfirm();
    }
  };

  const handleFinalConfirm = () => {
    setStep(1); // Reset for next time
    if (onConfirm) onConfirm();
  };

  const handleCancel = () => {
    setStep(1); // Reset for next time
    if (onCancel) onCancel();
  };

  if (!isOpen) {
    return null;
  }

  const modalTitle = (
    <>
      <MdWarning style={{ color: '#ff6b6b', marginRight: '8px' }} />
      {title}
    </>
  );

  const bodyContent = step === 1 ? (
    <>
      <span className="confirmMessage">{parseSanitizedHtml(message)}</span>
      {showSecondStep && (
        <span className="warningText">
          <MdWarning style={{ marginRight: '4px' }} />
          {dangerText}
        </span>
      )}
    </>
  ) : (
    <div className="finalConfirmation">
      <p className="confirmMessage">{secondStepMessage}</p>
      <span className="warningText">
        <MdWarning style={{ marginRight: '4px' }} />
        {secondStepWarning}
      </span>
      <div className="finalWarning">
        {secondStepDetail}
      </div>
    </div>
  );

  const footer = (
    <div className="footerRight">
      <button
        type="button"
        className="btn btn-secondary"
        onClick={handleCancel}
        style={{ display: showCancelButton ? undefined : 'none' }}
      >
        {cancelText}
      </button>
      {step === 1 && extraActions.map((action, index) => (
        <button
          key={`${action.label}-${index}`}
          type="button"
          className={`btn ${action.className || "btn-warning"}`}
          onClick={() => {
            setStep(1);
            action.onClick();
          }}
        >
          {action.label}
        </button>
      ))}
      <button
        type="button"
        className={`btn ${step === 1 ? 'btn-warning' : 'btn-danger'}`}
        onClick={step === 1 ? handleFirstConfirm : handleFinalConfirm}
      >
        {step === 1 ? confirmText : secondStepConfirmText}
      </button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      title={modalTitle}
      onClose={handleCancel}
      footer={footer}
      className="confirmModal" // Your custom class for styling
      zIndex={zIndex}
    >
      <div className="confirmContent">
        {bodyContent}
      </div>
    </Modal>
  );
}

